import { failures } from '@featit/shared/i18n/messages/en/failures';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { readerRefusalOf, type ReaderRefusalArgsType } from '@featit/shared/reader-refusal';
import { registerEnglishArea } from '@/i18n/catalogue';
import { englishT, type TFn } from '@/i18n/LocaleProvider';
import { pageLocale, pageT } from '@/i18n/pageTranslator';
import { refusalBodies } from './roleRefusalReason';

registerEnglishArea('failures', failures);

/**
 * I-2439: a signed-in refusal the server named by its code (`READER_REFUSALS`), said from
 * `failures.readerRefusals.*` in the reader's language. The server keeps its English sentence for the
 * CLI and for a client that does not know the code (F-0324: the reason is data, the sentence is the
 * client's).
 */
export interface IReaderRefusalReason {
  key: MessageKey;
  args: ReaderRefusalArgsType;
  /** The words a choice fact says, by fact (`READER_REFUSAL_CHOICES`), said before the sentence. */
  parts?: Readonly<Record<string, MessageKey>>;
}

/** Null for any failure that is not a coded refusal, or whose code this app does not know. */
export function readerRefusalReason(error: unknown): IReaderRefusalReason | null {
  for (const body of refusalBodies(error)) {
    // IN-101: a fact the server keeps in every language (a field's `{ en, de }` label) in the page's.
    const refusal = readerRefusalOf(body, pageLocale('en'));
    if (refusal) {
      return {
        key: `failures.readerRefusals.${refusal.key}` as MessageKey,
        args: refusal.args,
        parts: refusal.parts as Readonly<Record<string, MessageKey>>,
      };
    }
  }
  return null;
}

/**
 * The sentence a coded refusal says in the page's language, for a describer with no translator of its
 * own (`serverSentence`, `ApiService.httpErrorMessage`), as `roleRefusalSentence` does. Null for any
 * failure that is not one.
 */
export function readerRefusalSentence(error: unknown): string | null {
  const reason = readerRefusalReason(error);
  return reason ? sayReaderRefusal(pageT(englishT), reason) : null;
}

/**
 * A reader refusal said by `t`: its choice facts' parts first, in the same language, then the sentence
 * with them in place of the facts' raw values.
 */
export function sayReaderRefusal(
  t: TFn,
  reason: Pick<IReaderRefusalReason, 'key' | 'args' | 'parts'>,
): string {
  return t(reason.key, readerRefusalArgsIn(t, reason));
}

/** The facts a reader refusal's sentence takes, its choice facts said by `t` (`sayReaderRefusal`). */
export function readerRefusalArgsIn(
  t: TFn,
  reason: Pick<IReaderRefusalReason, 'args' | 'parts'>,
): Record<string, string | number> {
  const said = Object.fromEntries(
    Object.entries(reason.parts ?? {}).map(([fact, key]) => [fact, t(key, reason.args)]),
  );
  return { ...reason.args, ...said };
}
